// libs
import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { viewSharedProps } from '@aytm/app/stats/page/question-views/vk/lib/constants'
import { generateDefaultViewModel } from '@aytm/app/stats/page/question-views/vk/lib/model'
import { calcTableProps, getPointName } from '@aytm/app/stats/page/question-views/vk/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'

// components
import { Dropdown } from 'primereact/dropdown'
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import ChartButtons from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/ChartButtons'
import Text from '@aytm/app/stats/page/question-views/vk/components/text/Text'
import Chart from '@aytm/app/stats/page/question-views/vk/components/chart/Chart'
import ChartNote from '@aytm/app/stats/page/question-views/vk/components/chart-note/ChartNote'

// styles
import style from '@aytm/app/stats/page/question-views/vk/components/views/chart/styles/DefaultView.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const DefaultView = ({
  id,
  isLoading,
  isAnimated,
  type,
  components,
  period,
  palette,
  globalChartMode,
  defaults,
  answer,
  config,
  onModelChange,
  onExport
}) => {
  const { product, periodsList } = defaults
  const chartModel = generateDefaultViewModel({ id, product, answer, period, chartMode: globalChartMode })
  const isWestendorp = product === 1
  const isShowChartAndControls = isComponentOn(components, 'chart') && !isMapOrNone(globalChartMode)
  const isSelectedProducts = [2, 5].includes(product)

  const onSelectChange = event => {
    onModelChange({ [event.target.name]: String(event.value) })
  }

  const tableProps = calcTableProps({
    answer,
    period,
    isLoading,
    clickable: product !== 1 && isComponentOn(components, 'edit_controls'),
    filterRows: ['all'],
    onClick: position => {
      onModelChange({
        navto: getPointName({ answer, period, position }),
        infographic: true
      })
    }
  })

  return (
    // The view needs a root of its own: its sibling infographic view has one, and without it there
    // is no node to say the standard view is on screen -- it drew only a run of unrelated children.
    <div data-testid="stat-vk-default-view" className={style.view}>
      {isShowChartAndControls && !isSelectedProducts && !isWestendorp && !isLoading && (
        <div className={`${style.dropdownPanel} ${NO_PRINT}`}>
          <span className={style.dropdownTitle}>Timeframe/purchase frequency:</span>
          <Dropdown
            className={style.dropdown}
            aria-label="Timeframe/purchase frequency"
            ariaLabel="Timeframe/purchase frequency"
            name="period"
            value={period}
            options={periodsList}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            onChange={onSelectChange}
          />
        </div>
      )}
      {answer?.texts?.VWText && isWestendorp && !isLoading && (
        <Text className={style.text} value={answer?.texts?.VWText} />
      )}
      {isShowChartAndControls && (
        <ChartPlaceholder required isLoading={isLoading} isAnimated={isAnimated}>
          <Chart
            id={id}
            model={chartModel}
            palette={palette}
            captureIndex={0}
            infographic={false}
            isWestendorp={isWestendorp}
            onExport={onExport}
          />
        </ChartPlaceholder>
      )}
      {!isLoading && (
        <>
          {(answer?.total || 0) < 400 && (
            <ChartNote error>
              <>
                <Popup
                  trigger="click"
                  width="400px"
                  direction="righttop"
                  popupOffset={9}
                  hideOtherPopupsOnShow
                  content={() =>
                    `Price Sensitivity Meter Experiment${
                      answer?.omitVW ? '' : ' (PSM or Van Westendorp)'
                    } is a very efficient method to predict a price range/optimal price perception for a new product or service. Please remember that it only reports what the customers' expectations of the price will be. It is also very sensitive to number of completes so don't base any significant decisions on predictions with fewer responses than 400. We recommend using PSM as a directional tool or as a first step of price research, followed by a more precise methodology such as a monadic price test.`
                  }
                >
                  <i className="icon-aytmfd-help-v2" />
                </Popup>
                <b>Attention:</b>&nbsp;This experiment requires at least 400 completes. You are looking at{' '}
                {formatAmounts(answer?.total)} responses only.
              </>
            </ChartNote>
          )}
          <Text className={style.text} value={answer?.texts?.takeaway?.[`p${period}`]} />
          {isWestendorp ? null : (
            <ChartButtons
              id={id}
              answer={answer}
              config={config}
              components={components}
              period={period}
              type={type}
              product={product}
              onModelChange={onModelChange}
            />
          )}
          {Object.hasOwn(answer, 'pointsOfInterest') && (
            <Table cols={tableProps.cols} rows={tableProps.rows} classNames={tableProps.classNames} />
          )}
          {answer?.texts?.disclaimer && isWestendorp ? (
            <div className={style.text}>
              <p>
                <b>DISCLAIMER</b>
              </p>
              <Text value={answer?.texts?.disclaimer} />
            </div>
          ) : null}
          {answer?.footerText && !isWestendorp ? <ChartNote>{answer?.footerText}</ChartNote> : null}
        </>
      )}
    </div>
  )
}

DefaultView.displayName = 'DefaultView'

DefaultView.propTypes = {
  ...viewSharedProps
}

export default DefaultView
